// Artifact Type Definitions
// Immutable once approved, draft artifacts editable only by owner

/**
 * Artifact States
 */
export const ArtifactState = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ARCHIVED: 'archived'
};

/**
 * Artifact Types
 */
export const ArtifactType = {
  EXTERNAL_ORDER_REF: 'external_order_reference',
  SALES_TRANSACTION: 'sales_transaction',
  PAYMENT_RECEIPT: 'payment_receipt',
  SHIPPING_ARTIFACT: 'shipping_artifact',
  FINANCE_INTAKE_REQUEST: 'finance_intake_request'
};

/**
 * System Ownership
 */
export const SystemOwner = {
  SALES: 'sales',
  FINANCE: 'finance',
  SCM: 'scm',
  PAYPAL: 'paypal_external',
  EBAY: 'ebay_external'
};

/**
 * ExternalOrderReference
 * Owner: Sales
 * Source: eBay
 * Read-only
 */
export function createExternalOrderReference(data) {
  return {
    type: ArtifactType.EXTERNAL_ORDER_REF,
    owner: SystemOwner.EBAY,
    state: ArtifactState.APPROVED, // External refs are always "approved" (immutable)
    source: 'ebay',
    id: data.orderId,
    createdAt: data.timestamp || new Date().toISOString(),
    data: {
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      buyerUsername: data.buyerUsername,
      orderTotal: data.orderTotal,
      orderDate: data.orderDate,
      itemCount: data.itemCount,
      metadata: data.metadata || {}
    },
    readonly: true
  };
}

/**
 * SalesTransactionArtifact
 * Owner: Sales
 * Editable while Draft
 */
export function createSalesTransactionArtifact(data) {
  return {
    type: ArtifactType.SALES_TRANSACTION,
    owner: SystemOwner.SALES,
    state: ArtifactState.DRAFT,
    id: data.id || `ST-${Date.now()}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    data: {
      lineItems: data.lineItems || [],
      narrative: data.narrative || '',
      channelAttribution: data.channelAttribution || null, // e.g., "eBay → PayPal"
      customerReference: data.customerReference || null,
      externalOrderRef: data.externalOrderRef || null, // Link to ExternalOrderReference
      totalAmount: data.totalAmount || 0,
      transactionDate: data.transactionDate || new Date().toISOString(),
      metadata: data.metadata || {}
    },
    readonly: false,
    submittedToFinanceAt: null,
    approvedAt: null
  };
}

/**
 * PaymentReceiptArtifact
 * Owner: PayPal (external)
 * Read-only
 */
export function createPaymentReceiptArtifact(data) {
  return {
    type: ArtifactType.PAYMENT_RECEIPT,
    owner: SystemOwner.PAYPAL,
    state: ArtifactState.APPROVED, // External receipts are immutable
    id: data.transactionId,
    createdAt: data.timestamp || new Date().toISOString(),
    data: {
      transactionId: data.transactionId,
      grossAmount: data.grossAmount,
      fees: data.fees,
      netAmount: data.netAmount,
      currency: data.currency || 'USD',
      payerEmail: data.payerEmail,
      timestamp: data.timestamp,
      status: data.status || 'completed',
      metadata: data.metadata || {}
    },
    readonly: true
  };
}

/**
 * ShippingArtifact (SCM - Conceptual)
 * Owner: SCM
 * Read-only to Sales & Finance
 */
export function createShippingArtifact(data) {
  return {
    type: ArtifactType.SHIPPING_ARTIFACT,
    owner: SystemOwner.SCM,
    state: ArtifactState.APPROVED,
    id: data.id || `SHIP-${Date.now()}`,
    createdAt: data.timestamp || new Date().toISOString(),
    data: {
      carrier: data.carrier,
      trackingNumber: data.trackingNumber,
      shippingCost: data.shippingCost,
      shippingStatus: data.shippingStatus || 'pending',
      exceptions: data.exceptions || [], // damage, delays
      estimatedDelivery: data.estimatedDelivery,
      actualDelivery: data.actualDelivery || null,
      metadata: data.metadata || {}
    },
    readonly: true
  };
}

/**
 * FinanceIntakeRequest
 * Emitted by Sales when submitting artifacts to Finance
 * This is the "submit for approval" action
 */
export function createFinanceIntakeRequest(salesTransactionId, attachedArtifacts = []) {
  return {
    type: ArtifactType.FINANCE_INTAKE_REQUEST,
    owner: SystemOwner.SALES, // Originated by Sales
    targetSystem: SystemOwner.FINANCE,
    state: ArtifactState.SUBMITTED,
    id: `FIR-${Date.now()}`,
    createdAt: new Date().toISOString(),
    data: {
      salesTransactionId,
      attachedArtifacts, // Array of artifact references
      requestedBy: 'sales_user', // In real system, would be actual user
      submittedAt: new Date().toISOString(),
      metadata: {}
    },
    readonly: true // Once submitted, cannot be edited
  };
}

/**
 * Validation helpers
 */
export function canEditArtifact(artifact, currentSystem) {
  // Can only edit if:
  // 1. Artifact is owned by current system
  // 2. Artifact is in DRAFT state
  // 3. Artifact is not readonly
  return (
    artifact.owner === currentSystem &&
    artifact.state === ArtifactState.DRAFT &&
    artifact.readonly === false
  );
}

export function canSubmitArtifact(artifact, currentSystem) {
  // Can submit if:
  // 1. Artifact is owned by current system
  // 2. Artifact is in DRAFT state
  return (
    artifact.owner === currentSystem &&
    artifact.state === ArtifactState.DRAFT
  );
}

export function isArtifactImmutable(artifact) {
  return (
    artifact.readonly === true ||
    artifact.state === ArtifactState.APPROVED ||
    artifact.state === ArtifactState.ARCHIVED
  );
}
